import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Container,
  List,
  ListItem,
  ListItemText,
  Typography
} from '@mui/material';
import { PollResponse, ResponseValue, Voter } from '../../types';
import React, { useState } from 'react';
import { SearchParams, searchIndex, splitKeywords } from '../../utils/indexer';
import { Timestamp, collection, doc, getDoc, setDoc } from 'firebase/firestore';

import { db } from '../../firebase';
import { useAuth } from '../../hooks/useAuth';
import { v4 as uuidv4 } from 'uuid';

interface Responses {
  [question: string]: ResponseValue;
}

const DelCarmenImport: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const { user } = useAuth();
  const [notFoundList, setNotFoundList] = useState<string[]>([]);

  const dashboardId = 'SURIGAO DEL NORTE_DISTRICT 1';
  
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setError(null);
      setSuccess(null);
    }
  };

  const searchVoter = async (keyword: string, municipality: string): Promise<Voter | null> => {
    const keywords = splitKeywords(keyword);
    const searchParams: SearchParams = {
      keywords,
      resultLimit: 10,
      document: "voter",
      field: "name",
      searchAfter: null
    };
    let result = await searchIndex(searchParams);
    while ((result.indexes.length <= 0) && (keywords.length > 2)) {
      keywords.pop();
      searchParams.keywords = keywords;
      result = await searchIndex(searchParams);
    }
    
    for (const index of result.indexes) {
      const voterDocRef = doc(db, index.documentPath);
      const voterDoc = await getDoc(voterDocRef);
      if (voterDoc.exists()) {
        const voter = voterDoc.data() as Voter;
        if (voter.city_municipality === municipality) {
          return voter;
        }
      }
    }

    return null;
  };

  const wasPolledForLucille = async (voter: Voter): Promise<boolean> => {
    try {
      const latestPollResponsePath = 
        `tabulationsV2/${dashboardId}/regions/${voter.region}/`
        + `provinces/${voter.province}/districts/${voter.district}/`
        + `cities/${voter.city_municipality}/barangays/${voter.barangay}/`
        + `precincts/${voter.precinct}/voters/${voter.name}`;

      const latestPollResponseRef = doc(db, latestPollResponsePath);
      const latestPollResponseDoc = await getDoc(latestPollResponseRef);
      if (latestPollResponseDoc.exists()) {
        const latestPollResponse = latestPollResponseDoc.data() as PollResponse;
        return latestPollResponse.responses['DISTRICT 1 REPRESENTATIVE'] === 'Lucille Sering';
      }
    } catch (error) {
      console.error('Error checking if voter was polled before: ', error);
    }
    return false;
  };

  const submitPollResponse = async (responses: Responses): Promise<PollResponse | null> => {
    try {
      const responseId = uuidv4();
      const pollResponse: PollResponse = {
        id: responseId,
        pollId: "32aeb5b7-7227-4efb-8080-ec4ccff14fc4",
        pollTitle: "Del Carmen (VIS)",
        responses,
        submittedAt: Timestamp.now(),
        submittedBy: user?.display_name ?? 'Keeta Developer',
      };
      
      const responseRef = doc(collection(db, 'pollResponses'), responseId);
      await setDoc(responseRef, pollResponse);
      
      return pollResponse;
    } catch (error) {
      console.error('Error submitting poll response: ', error);
    }
    
    return null;
  };

  const checkVoter = async (name: string) => {
    try {
      const nameWithoutLeader = name.replace(/^Leader: /, '');
      const voter = await searchVoter(nameWithoutLeader, 'DEL CARMEN');
      if (voter) {
        const isForLucille = await wasPolledForLucille(voter);
        if (!isForLucille) {
          const abante: Responses = {
            "DISTRICT 1 REPRESENTATIVE": "Lucille Sering",
            "MAYOR": "Molly Sering",
            "PARTY": "Abante Surigao",
            "PROVINCIAL GOVERNOR": "Lyndon Barbers",
            "PROVINCIAL VICE GOVERNOR": "Geed Gokiangkee",
            "VOTER": voter.name,
          };          
          await submitPollResponse(abante);
          console.log(`Voter polled: ${voter.name}`);
        } else {
          console.log(`Voter already polled: ${voter.name}`);
        }
      } else {
        console.error('Voter not found:', nameWithoutLeader);
        setNotFoundList((prev) => [...prev, nameWithoutLeader]);
      }
    } catch (err) {
      console.error('Error checking voter:', err);
      throw err;
    }
  };

  const handleImport = async () => {
    if (!user) {
      setError('You must be logged in to import voters');
      return;
    }

    if (!file) {
      setError('Please select a file to import');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);
    setNotFoundList([]); // Reset not found list before import

    try {
      const reader = new FileReader();
      
      reader.onload = async (e) => {
        const csvData = e.target?.result as string;
        const lines = csvData.split('\n').filter(line => line.trim() !== '');
        
        // Skip header if present
        const dataLines = lines[0].includes(',') ? lines.slice(1) : lines;
        
        for (const line of dataLines) {
          try {
            await checkVoter(line.trim());
          } catch (err) {
            console.error(`Error processing line: ${line}`, err);
          }
        }
        
        setLoading(false);
        setSuccess(`Import completed. ${notFoundList.length} voters not found.`);
      };

      reader.onerror = (error) => {
        console.error('Error reading file:', error);
        setError('Error reading file');
        setLoading(false);
      };

      reader.readAsText(file);
    } catch (error) {
      console.error('Error preparing import:', error);
      setError(`Preparation error: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box sx={{ my: 4 }}>
        <Typography variant="h4" gutterBottom>
          Del Carmen Voters CSV Import
        </Typography>
        
        <input
          accept=".csv"
          style={{ display: 'none' }}
          id="del-carmen-csv-upload"
          type="file"
          onChange={handleFileChange}
        />
        <label htmlFor="del-carmen-csv-upload">
          <Button 
            variant="contained" 
            component="span" 
            color="primary"
            sx={{ mb: 2, mr: 2 }}
          >
            Select CSV File
          </Button>
        </label>

        {file && (
          <Typography variant="body2" sx={{ mb: 2 }}>
            Selected file: {file.name}
          </Typography>
        )}

        <Button
          variant="contained"
          color="secondary"
          onClick={handleImport}
          disabled={!file || loading}
          sx={{ mb: 2 }}
        >
          {loading ? <CircularProgress size={24} /> : 'Import Voters'}
        </Button>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            <AlertTitle>Error</AlertTitle>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mt: 2 }}>
            <AlertTitle>Success</AlertTitle>
            {success}
          </Alert>
        )}

        {notFoundList.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom>
              Voters Not Found
            </Typography>
            <List dense>
              {notFoundList.map((name, index) => (
                <ListItem key={index}>
                  <ListItemText primary={name} />
                </ListItem>
              ))}
            </List>
          </Box>
        )}
      </Box>
    </Container>
  );
};

export default DelCarmenImport;
